import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { useAdTracking, trackAdClick } from '@/hooks/useAdTracking';
import { useAuth } from '@/hooks/useAuth';

interface Advertisement {
  id: string;
  title: string;
  description: string | null;
  image_url: string | null;
  link_url: string | null;
  position: string;
  promotion_type?: string | null;
}

interface AdvertisementDisplayProps {
  position: 'top' | 'middle' | 'bottom' | 'sidebar';
  className?: string;
}

export const AdvertisementDisplay = ({ position, className = '' }: AdvertisementDisplayProps) => {
  const [ad, setAd] = useState<Advertisement | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Track impression when ad is displayed
  useAdTracking(ad?.id || '', 'impression');

  useEffect(() => {
    fetchAd();
    
    // Real-time subscription for ads
    const channel = supabase
      .channel('advertisements-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'advertisements'
        },
        () => {
          fetchAd();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [position]);

  const fetchAd = async () => {
    try {
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from('advertisements')
        .select('*')
        .eq('position', position)
        .eq('active', true)
        .lte('start_date', now)
        .or(`end_date.is.null,end_date.gte.${now}`)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Erreur chargement publicité:', error);
        setAd(null);
        return;
      }

      setAd(data);
      setDismissed(false);
    } catch (error) {
      console.error('Erreur chargement publicité:', error);
      setAd(null);
    }
  };

  if (!ad || dismissed) {
    return null;
  }

const handleClick = () => {
  if (!ad?.id) return;
  trackAdClick(ad.id, user?.id);
  
  // Always navigate to promotion page if it has promotion_type
  if (ad.promotion_type) {
    navigate(`/promo/${ad.id}`);
    return;
  }
  
  // If no link_url, default to promotion page
  if (!ad.link_url) {
    navigate(`/promo/${ad.id}`);
    return;
  }
  
  // Navigate internally for app routes
  if (ad.link_url.startsWith('/')) {
    navigate(ad.link_url);
  } else {
    // For external links, open in same window
    window.location.href = ad.link_url;
  }
};

  return (
    <div className={`relative rounded-lg overflow-hidden shadow-lg ${className}`}>
      <Button
        variant="ghost"
        size="sm"
        className="absolute top-2 right-2 z-10 bg-white/80 hover:bg-white"
        onClick={() => setDismissed(true)}
      >
        <X className="h-4 w-4" />
      </Button>

      <div
        className={`${ad.link_url ? 'cursor-pointer' : ''} transition-transform hover:scale-[1.02]`}
        onClick={handleClick}
      >
        {ad.image_url && (
          <img
            src={ad.image_url}
            alt={ad.title}
            className="w-full h-auto object-cover"
          />
        )}
        
        <div className="p-4 bg-gradient-to-r from-primary/10 to-primary/5">
          <h3 className="text-lg font-semibold text-foreground mb-2">
            {ad.title}
          </h3>
          {ad.description && (
            <p className="text-sm text-muted-foreground">
              {ad.description}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
